import type { AvailableUnits } from '@cw-elements/profile-dropdown/utils/contants';
import convert from 'convert-units';
import { memoize } from 'lodash';
import { UnitSystem } from './constants.ts';
import { getCurrentLanguage } from './i18n.ts';
import { parseNumber } from './number.ts';

const UNIT_SYSTEM_STORAGE_KEY = 'unitSystem';

function getCurrentUnitSystemFromStorage(): AvailableUnits {
  try {
    return (localStorage.getItem(UNIT_SYSTEM_STORAGE_KEY) as AvailableUnits) ?? UnitSystem.METRIC;
  } catch {
    return UnitSystem.METRIC;
  }
}

let cachedUnitSystem: AvailableUnits | undefined;
const unitSystemListeners = new Set<() => void>();

export const getCurrentUnitSystem = (): AvailableUnits => {
  cachedUnitSystem ??= getCurrentUnitSystemFromStorage();

  return cachedUnitSystem;
};

export const setCurrentUnitSystem = (unitSystem: AvailableUnits) => {
  if (cachedUnitSystem === unitSystem) return;

  cachedUnitSystem = unitSystem;

  try {
    localStorage.setItem(UNIT_SYSTEM_STORAGE_KEY, unitSystem);
  } catch {
    // A storage write failure must not stop the in-memory value from propagating.
  }

  for (const listener of unitSystemListeners) listener();
};

export const subscribeToUnitSystem = (listener: () => void) => {
  unitSystemListeners.add(listener);

  return () => {
    unitSystemListeners.delete(listener);
  };
};

export const resetUnitSystemCache = () => {
  cachedUnitSystem = undefined;
};

const createNumberFormatter = (locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat => {
  return new Intl.NumberFormat(locale, options);
};

// Cache key covers only minimumFractionDigits and maximumFractionDigits — the only options passed by formatNumber (sole call site).
const getOptimizedNumberFormatter = memoize(
  createNumberFormatter,
  (locale: string, options: Intl.NumberFormatOptions) => `${locale}-${options.minimumFractionDigits}-${options.maximumFractionDigits}`
);

export const formatNumber = (number: number | string | undefined, min = 2, max = 2): string => {
  if (number === undefined || number === null) return '';
  const parsedNumber = parseNumber(number);

  if (Number.isNaN(parsedNumber)) {
    return '';
  }

  const minimumFractionDigits = Math.max(0, Math.min(min, 18));
  const maximumFractionDigits = Math.max(0, Math.min(max, 18));

  const currentLanguage = getCurrentLanguage();
  const formatter = getOptimizedNumberFormatter(currentLanguage, {
    minimumFractionDigits,
    maximumFractionDigits
  });

  return formatter.format(parsedNumber);
};

export const formatArea = (value: number | string, min = 2, max = 2, from: convert.Unit = 'ha', to: convert.Unit = 'ac') => {
  if (!value) return '0.00';
  const unitSystem = getCurrentUnitSystem();
  let areaValue = parseNumber(value);
  if (unitSystem === UnitSystem.IMPERIAL) areaValue = convert(areaValue).from(from).to(to);

  return formatNumber(areaValue, min, max);
};
